import React from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { FALLBACK_PROJECT_COLOR, secondsToHMS } from 'shared/components/charts';
import { displayDuration } from 'shared/lib/formatDuration';

import { ChartOuterBox, ChartTitle } from '../../reports/reports.styles';
import {
  DonutCard,
  DonutCenterLabel,
  DonutCenterOverlay,
  DonutOuterRow,
  DonutScrollInner,
  DonutSidePanel,
  DonutTooltipBox,
  DonutTooltipDot,
  DonutTooltipName,
  DonutTooltipRow,
  DonutTooltipValue,
  DonutWrapper,
} from '../dashboard.styles';
import type { DashboardProjectEntry } from '../types/dashboard.types';

import DashboardProjectList from './DashboardProjectList';

interface Props {
  projects: DashboardProjectEntry[];
  totalSecs: number;
}

const DashboardDonutChart: React.FC<Props> = ({ projects, totalSecs }) => {
  const slices = projects.map((p) => ({
    id: p.projectId,
    name: p.name,
    color: p.color ?? FALLBACK_PROJECT_COLOR,
    value: p.totalSecs,
    percentage: p.percentage,
  }));

  return (
    <ChartOuterBox>
      <DonutOuterRow>
        {/* Donut card */}
        <DonutCard>
          <ChartTitle sx={{ mb: 2 }}>Project Distribution</ChartTitle>
          <DonutWrapper>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={
                    slices.length > 0
                      ? slices
                      : [{ id: 'empty', name: '', color: '#eee', value: 1, percentage: 0 }]
                  }
                  cx="50%"
                  cy="50%"
                  innerRadius="52%"
                  outerRadius="80%"
                  dataKey="value"
                  paddingAngle={slices.length > 1 ? 1 : 0}
                  startAngle={90}
                  endAngle={-270}
                  strokeWidth={0}
                  isAnimationActive
                >
                  {(slices.length > 0 ? slices : [{ id: 'empty', color: '#eee' }]).map((slice) => (
                    <Cell key={slice.id} fill={slice.color} stroke="none" />
                  ))}
                </Pie>
                <Tooltip
                  wrapperStyle={{ zIndex: 10 }}
                  content={({ payload }) => {
                    const item = payload?.[0]?.payload;
                    if (!item || !item.name) return null;
                    return (
                      <DonutTooltipBox>
                        <DonutTooltipRow>
                          <DonutTooltipDot dotcolor={item.color} />
                          <DonutTooltipName>{item.name}</DonutTooltipName>
                        </DonutTooltipRow>
                        <DonutTooltipValue>
                          {displayDuration(secondsToHMS(item.value))} · {item.percentage}%
                        </DonutTooltipValue>
                      </DonutTooltipBox>
                    );
                  }}
                />
              </PieChart>
            </ResponsiveContainer>

            <DonutCenterOverlay>
              <DonutCenterLabel>{displayDuration(secondsToHMS(totalSecs))}</DonutCenterLabel>
            </DonutCenterOverlay>
          </DonutWrapper>
        </DonutCard>

        {/* Project list card */}
        <DonutSidePanel>
          <DonutScrollInner>
            <DashboardProjectList projects={projects} />
          </DonutScrollInner>
        </DonutSidePanel>
      </DonutOuterRow>
    </ChartOuterBox>
  );
};

export default DashboardDonutChart;
